import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Afiliado {
  id: string;
  asesor_id?: string;
  nombre: string;
  correo?: string;
  telefono?: string;
  calificacion?: number;
  inmobiliaria_doc_ids?: string[];
  activo?: boolean;
  propsActivas?: number;
  inmobiliaria?: string;
  [key: string]: any;
}

export default function AdminAfiliado() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [afiliados, setAfiliados] = useState<Afiliado[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroInmobiliaria, setFiltroInmobiliaria] = useState('');
  const [loading, setLoading] = useState(true);
  const [paginaActual, setPaginaActual] = useState(1);
  const afiliadosPorPagina = 10;

  useEffect(() => {
    if (!user) return;

    const fetchAfiliados = async () => {
      if (!Array.isArray(user.tipo) || !user.tipo.includes('admin')) {
        Swal.fire({
          icon: 'error',
          title: 'Acceso denegado',
          text: 'No tienes permiso para ver esta sección.'
        });
        return navigate('/');
      }

      try {
        // ✅ Cargar todos los asesores (filtraremos activos después)
        const asesorSnap = await getDocs(collection(db, 'asesor'));

        // Filtrar solo activos o sin estatus (por defecto activos)
        const asesorDocs = asesorSnap.docs.filter(docSnap => {
          const data = docSnap.data();
          return !data.estatus || data.estatus === 'activo';
        });

        const lista = await Promise.all(
          asesorDocs.map(async (docSnap) => {
            const data = docSnap.data();

            // ✅ Contar propiedades usando doc.id del asesor
            let propsActivas = 0;
            try {
              const propsSnap = await getDocs(
                query(
                  collection(db, 'propiedades'),
                  where('asesor_doc_ids', 'array-contains', docSnap.id),
                  where('activo', '==', true)
                )
              );
              propsActivas = propsSnap.docs.length;
            } catch (error) {
              console.error(`Error contando propiedades para ${data.nombre}:`, error);
            }

            // ✅ Buscar inmobiliaria usando inmobiliaria_doc_ids
            let inmobiliaria = 'Sin inmobiliaria';
            if (data.inmobiliaria_doc_ids && Array.isArray(data.inmobiliaria_doc_ids) && data.inmobiliaria_doc_ids.length > 0) {
              try {
                const inmobiliariaDocId = data.inmobiliaria_doc_ids[0];
                const inmoDocRef = doc(db, 'inmobiliaria', inmobiliariaDocId);
                const inmoSnap = await getDoc(inmoDocRef);

                if (inmoSnap.exists()) {
                  inmobiliaria = inmoSnap.data().nombre || 'Sin nombre';
                }
              } catch (error) {
                console.error(`Error buscando inmobiliaria para ${data.nombre}:`, error);
              }
            }

            return {
              id: docSnap.id,
              nombre: data.nombre,
              correo: data.correo,
              telefono: data.telefono,
              calificacion: data.calificacion,
              inmobiliaria_doc_ids: data.inmobiliaria_doc_ids,
              activo: data.activo,
              propsActivas,
              inmobiliaria,
              ...data
            } as Afiliado;
          })
        );

        // ✅ Ordenar alfabéticamente por nombre
        lista.sort((a, b) => {
          const nombreA = (a.nombre || '').toLowerCase();
          const nombreB = (b.nombre || '').toLowerCase();
          return nombreA.localeCompare(nombreB);
        });

        setAfiliados(lista);
      } catch (err) {
        console.error('Error al cargar afiliados:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la lista de afiliados.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAfiliados();
  }, [user, navigate]);

  // ✅ Filtros aplicados
  const afiliadosFiltrados = afiliados.filter(a => {
    const matchNombre = a.nombre?.toLowerCase().includes(filtro.toLowerCase());
    const matchCorreo = a.correo?.toLowerCase().includes(filtro.toLowerCase());
    const matchInmobiliaria = filtroInmobiliaria === '' || a.inmobiliaria?.toLowerCase().includes(filtroInmobiliaria.toLowerCase());

    return (matchNombre || matchCorreo) && matchInmobiliaria;
  });

  // ✅ Paginación
  const totalPaginas = Math.ceil(afiliadosFiltrados.length / afiliadosPorPagina);
  const startIndex = (paginaActual - 1) * afiliadosPorPagina;
  const afiliadosPaginados = afiliadosFiltrados.slice(startIndex, startIndex + afiliadosPorPagina);

  // ✅ Obtener inmobiliarias únicas para filtro
  const inmobiliariasUnicas = Array.from(new Set(
    afiliados.map(a => a.inmobiliaria).filter(Boolean)
  )).sort();

  const handleEliminar = async (id: string, nombre: string) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar afiliado?',
      html: `¿Estás seguro de eliminar a <strong>${nombre}</strong>?<br>Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'asesor', id));
        setAfiliados(prev => prev.filter(a => a.id !== id));

        Swal.fire({
          icon: 'success',
          title: 'Eliminado',
          text: `El afiliado ${nombre} fue eliminado exitosamente.`
        });
      } catch (error) {
        console.error('Error eliminando afiliado:', error);
        Swal.fire('Error', 'No se pudo eliminar el afiliado. Intenta nuevamente.', 'error');
      }
    }
  };

  if (!user) {
    return (
      <div className="container mt-5">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (!user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center">
          <h4>Acceso denegado</h4>
          <p>No tienes permiso para ver esta sección.</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container mt-5 mb-5">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Administración de Afiliados' }
          ]}
        />

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">
            <i className="fas fa-user-tie me-2 text-primary"></i>
            Administración de Afiliados
          </h2>
          <button className="btn btn-success" onClick={() => navigate('/add-agent')}>
            <i className="fas fa-plus me-2"></i>
            Agregar Afiliado
          </button>
        </div>

        {/* Estadísticas */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center bg-primary text-white shadow-sm">
              <div className="card-body">
                <h3>{afiliados.length}</h3>
                <p className="mb-0">Total Afiliados</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-success text-white shadow-sm">
              <div className="card-body">
                <h3>{afiliados.filter(a => a.propsActivas && a.propsActivas > 0).length}</h3>
                <p className="mb-0">Con Propiedades</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-info text-white shadow-sm">
              <div className="card-body">
                <h3>{afiliados.filter(a => a.calificacion && a.calificacion >= 80).length}</h3>
                <p className="mb-0">Calificación 4+</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-warning text-white shadow-sm">
              <div className="card-body">
                <h3>{afiliadosFiltrados.length}</h3>
                <p className="mb-0">Filtrados</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-5">
                <label className="form-label">Buscar por nombre o correo:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar afiliados..."
                  value={filtro}
                  onChange={e => {
                    setFiltro(e.target.value);
                    setPaginaActual(1);
                  }}
                />
              </div>
              <div className="col-md-5">
                <label className="form-label">Filtrar por inmobiliaria:</label>
                <select
                  className="form-select"
                  value={filtroInmobiliaria}
                  onChange={e => {
                    setFiltroInmobiliaria(e.target.value);
                    setPaginaActual(1);
                  }}
                >
                  <option value="">Todas las inmobiliarias</option>
                  {inmobiliariasUnicas.map(inmo => (
                    <option key={inmo} value={inmo}>{inmo}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={() => {
                    setFiltro('');
                    setFiltroInmobiliaria('');
                    setPaginaActual(1);
                  }}
                >
                  <i className="fas fa-times me-1"></i>Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3">Cargando afiliados...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered table-hover shadow-sm">
                <thead className="table-dark">
                  <tr>
                    <th>Nombre</th>
                    <th>Correo</th>
                    <th>Teléfono</th>
                    <th>Agencia</th>
                    <th>Calificación</th>
                    <th>Propiedades</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {afiliadosPaginados.length > 0 ? (
                    afiliadosPaginados.map(af => (
                      <tr key={af.id}>
                        <td>
                          <i className="fas fa-user-circle me-2 text-muted"></i>
                          <strong>{af.nombre}</strong>
                        </td>
                        <td>{af.correo || <span className="text-muted">No disponible</span>}</td>
                        <td>{af.telefono || <span className="text-muted">No disponible</span>}</td>
                        <td>
                          <i className="fas fa-building me-1 text-primary"></i>
                          {af.inmobiliaria || 'Sin asignar'}
                        </td>
                        <td className="text-center">
                          {af.calificacion ? (
                            <span className="badge bg-info">
                              <i className="fas fa-star me-1"></i>
                              {(af.calificacion / 20).toFixed(1)}/5
                            </span>
                          ) : (
                            <span className="badge bg-secondary">Sin calificar</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={`badge ${af.propsActivas && af.propsActivas > 0 ? 'bg-success' : 'bg-secondary'}`}>
                            {af.propsActivas || 0}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => navigate(`/ver-admin-afiliado/${af.id}`)}
                              title="Ver detalles"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => navigate(`/editar-afiliado/${af.id}`)}
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleEliminar(af.id, af.nombre)}
                              title="Eliminar"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                        <p className="mb-0">
                          {filtro || filtroInmobiliaria
                            ? 'No se encontraron afiliados con ese criterio de búsqueda.'
                            : 'No hay afiliados registrados.'}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPaginas > 1 && (
              <nav className="d-flex justify-content-center mt-4" aria-label="Paginación de afiliados">
                <ul className="pagination flex-wrap">
                  <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPaginaActual(paginaActual - 1)}
                      disabled={paginaActual === 1}
                    >
                      <i className="fas fa-chevron-left me-1"></i>Anterior
                    </button>
                  </li>

                  {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                    let pageNum: number;
                    if (totalPaginas <= 5) {
                      pageNum = i + 1;
                    } else if (paginaActual <= 3) {
                      pageNum = i + 1;
                    } else if (paginaActual >= totalPaginas - 2) {
                      pageNum = totalPaginas - 4 + i;
                    } else {
                      pageNum = paginaActual - 2 + i;
                    }

                    return (
                      <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPaginaActual(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}

                  <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPaginaActual(paginaActual + 1)}
                      disabled={paginaActual === totalPaginas}
                    >
                      Siguiente<i className="fas fa-chevron-right ms-1"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}

            {/* Información de paginación */}
            <div className="text-center text-muted">
              Mostrando {startIndex + 1} a {Math.min(startIndex + afiliadosPorPagina, afiliadosFiltrados.length)} de {afiliadosFiltrados.length} afiliados
            </div>
          </>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
